import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/Accordion";

import Button from "../components/Button";
import PetHeader from "../components/PetHeader";
import StickyScrollWrapper from "../components/StickyScrollWrapper";
import { getPetByID } from "../services/api";

const staticUrl: string = process.env.STATIC_URL;

export default function Pet() {
  const [loading, setLoading] = useState(true);
  const [pet, setPet] = useState({});
  const navigate = useNavigate();
  const { id } = useParams<{
    id: string;
  }>();
  const containerRef = useRef(null);

  useEffect(() => {
    console.log(pet);
  }, [pet]);

  useEffect(() => {
    void getPetByID(id)
      .then((data) => setPet(data))
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!pet.images || loading) {
      return;
    }
    window.scrollTo(0, 0);
  }, [pet.images, loading]);

  const getNav = () => {
    return [
      { title: "About dog", url: `/pet/${id}`, active: true },
      { title: "About shelter", url: `/pet/${id}/shelter` },
    ];
  };

  const getParamsList = () => {
    const list = [{ title: "Dog code", value: pet.petCode }];

    if (pet.birthdate) {
      list.push({ title: "Birth date", value: pet.birthdate });
    }

    if (pet.travelRequirements?.chip) {
      list.push({ title: "Chip", value: "Yes" });
    }

    if (pet.travelRequirements?.passport) {
      list.push({ title: "Pet passport", value: "Yes" });
    }

    if (pet.treatments?.flea) {
      list.push({ title: "Flea and Tick Treatment", value: "Yes" });
    }

    if (pet.treatments?.deworming) {
      list.push({ title: "Deworming", value: "Yes" });
    }

    return list;
  };

  const getVaccinesList = () => {
    const list = [];
    if (pet.vaccines?.dhpp) {
      list.push("DHPP");
    }
    if (pet.vaccines?.rabies) {
      list.push("Rabies");
    }
    return list;
  };

  if (!pet || loading) {
    return null;
  }

  return (
    <>
      <div className="container mx-auto px-5">
        <div className="mb-6 text-theme-grey-7">
          <Link to="/">Home</Link> / <span className="text-theme-grey-11">{pet.name}</span>
        </div>
        <PetHeader pet={pet}>
          <Button
            title="Explore Adoption"
            onClick={() => navigate(`/adopt/${pet.id}`)}
            theme="dark"
          />
        </PetHeader>
      </div>

      <div className="relative my-8 border-b-[1px] border-theme-grey-1">
        <div className="container mx-auto flex flex-col items-center justify-between gap-4 px-5 lg:flex-row">
          <div className="flex h-full w-full flex-row items-center gap-6">
            {getNav().map((i) => (
              <div key={i.title} className="flex h-full items-center">
                <Link
                  to={i.url}
                  className={`cursor-pointer border-b-2 border-transparent pb-1 font-semibold text-theme-grey-11 hover:border-[#000000] ${
                    i.active && "!border-[#000000]"
                  }`}
                >
                  {i.title}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto mb-8 px-5">
        <div className="grid grid-cols-3 gap-5">
          <div
            id="pet-scroll"
            className="pet-images-scroll relative col-span-2 flex flex-col gap-5"
          >
            {pet.images &&
              [...pet.images]
                .filter((i) => !i.main)
                .sort((a, b) => {
                  if (a.main) {
                    return -1;
                  } else if (b.main) {
                    return 1;
                  }
                  return 0;
                })
                .map((i) => (
                  <img key={i.url} src={`${staticUrl}${i.url}`} className="w-full rounded-[4px]" />
                ))}
          </div>
          <div className="relative flex flex-col gap-5" ref={containerRef}>
            <StickyScrollWrapper refId="footer">
              <Accordion type="single" collapsible className="w-full" defaultValue="item-2">
                <AccordionItem value="item-1">
                  <AccordionTrigger>Description</AccordionTrigger>
                  <AccordionContent>
                    <div
                      className="flex flex-col gap-6 whitespace-pre-wrap leading-[135%] text-[#5C5C5C]"
                      dangerouslySetInnerHTML={{
                        __html: pet.desc,
                      }}
                    />
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-2">
                  <AccordionTrigger>Details</AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-2 border-l-[1px] border-t-[1px] border-[#DBDBDB]">
                      {getParamsList().map((p) => (
                        <div
                          key={p.title}
                          className="border-b-[1px] border-r-[1px] border-[#DBDBDB] p-3 text-[#242424]"
                        >
                          <div className="font-barlowmedium">{p.title}</div>
                          {p.value}
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
                {getVaccinesList().length > 0 && (
                  <AccordionItem value="item-3">
                    <AccordionTrigger>Vaccinations</AccordionTrigger>
                    <AccordionContent>
                      <div className="mb-4 flex flex-row flex-wrap gap-2">
                        {getVaccinesList().map((v) => (
                          <div
                            key={v}
                            className="inline-block rounded-[5px] bg-[#F8F5F2] px-4 py-2 font-barlowmedium text-[#242424]"
                          >
                            {v}
                          </div>
                        ))}
                      </div>
                      {Math.round(Math.random()) ? (
                        <div className="mb-4">
                          <div className="flex items-center gap-1 text-[12px]">
                            <svg width="10" height="9" viewBox="0 0 10 9" fill="none">
                              <path
                                d="M1 3.08696L2.97763 7.36773C3.30648 8.07956 4.28935 8.15321 4.72061 7.49833L9 1"
                                stroke="#096807"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                            </svg>
                            <div>
                              Meets all{" "}
                              <span className="font-barlowmedium">
                                EU requirements for dog vaccinations
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="mb-4 inline-block">
                          <div className="flex items-center gap-1 border-b-[1px] border-[#F6520B] pb-2 text-[12px] text-[#F6520B]">
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                              <path
                                d="M8.07129 1.46436L1.00022 8.53542"
                                stroke="#F6520B"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                              <path
                                d="M8.07129 8.53564L1.00022 1.46458"
                                stroke="#F6520B"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                            </svg>

                            <div>Does not yet meet all EU requirements for dog vaccinations.</div>
                          </div>
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                )}

                <AccordionItem value="item-4">
                  <AccordionTrigger>Ask shelter about this dog</AccordionTrigger>
                  <AccordionContent>
                    <div className="flex flex-col gap-4">
                      <textarea
                        rows={5}
                        className="w-full resize-none rounded-[4px] border-[1px] border-[#DBDBDB] p-[10px] outline-0"
                        placeholder="Message"
                      />
                      <Button title="Send" onClick={() => ""} theme="dark" />
                    </div>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-5">
                  <AccordionTrigger>Share this dog with a friend</AccordionTrigger>
                  <AccordionContent>
                    <Button
                      title="Share"
                      onClick={() => ""}
                      theme="transparent"
                      buttonClasses="w-full"
                    />
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </StickyScrollWrapper>
          </div>
        </div>
      </div>
    </>
  );
}
